import type { FieldWarning } from "@/game_profiles/types";

/**
 * Validation for the RuneScape: Dragonwilds `OwnerId`, an EOS Online ID.
 *
 * The server refuses to operate without one and never appears in the in-game browser, so a server
 * saved with an empty or malformed ID can never work. The panel warns rather than blocks: Jagex
 * have announced the field will become optional, with the server claimed by admin password on
 * first join instead, and a hard gate would be wrong the moment that ships.
 */

/**
 * 32 lowercase hex characters.
 *
 * The lowercase requirement is load-bearing, not cosmetic. `ServerGuid` sits in the same section of
 * the same file and is also 32 hex characters, but uppercase and generated by the game. A
 * case-insensitive test would happily accept a pasted `ServerGuid`, which looks valid and produces
 * exactly the broken server this validation exists to catch.
 */
const OWNER_ID_PATTERN = /^[0-9a-f]{32}$/;

export type OwnerIdProblem = "empty" | "malformed";

/**
 * Returns what is wrong with an `OwnerId`, or `null` when it is a valid EOS Online ID.
 *
 * Anything that is not a string counts as empty rather than malformed: an absent value reads to the
 * customer as "you have not filled this in", which is the more useful of the two messages.
 */
export function getOwnerIdProblem(value: unknown): OwnerIdProblem | null {
  if (typeof value !== "string" || value.trim() === "") return "empty";
  if (!OWNER_ID_PATTERN.test(value)) return "malformed";
  return null;
}

/** `fieldWarnings` check for the `OwnerId` field. */
export function getOwnerIdWarning(value: unknown): FieldWarning | null {
  const problem = getOwnerIdProblem(value);
  return problem ? { key: `ngp/runeScapeDragonwilds/ownerid/${problem}` } : null;
}

/** Convenience wrapper for callers that only need a yes or no, not which problem it is. */
export function isValidOwnerId(value: unknown): boolean {
  return getOwnerIdProblem(value) === null;
}
